import { useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CheckIcon } from '@phosphor-icons/react';
import { LtdService, type LtdPublicTier } from '@/api/ltd-service';
import { PromoService } from '@/api/promo-service';
import { LinkLogo } from '@/components/nav/link-logo';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Loader } from '@/components/ui/loader';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/utils/cn';

export const Route = createFileRoute('/deal')({ component: DealPage });

const money = (cents: number) => `$${(cents / 100).toLocaleString('en-US')}`;
const compact = (n: number) =>
  n >= 1_000_000 ? `${n / 1_000_000}M` : n >= 1_000 ? `${n / 1_000}k` : String(n);

const FAQ: { q: string; a: string }[] = [
  {
    q: 'Is this really one payment, forever?',
    a: 'Yes. Pay once and keep your plan for the life of the product, up to your monthly event limit. There is no recurring subscription.',
  },
  {
    q: "What if I don't know my domain yet?",
    a: 'No problem. Sign up first and add or change your tracking domain anytime in Settings.',
  },
  {
    q: 'What happens if I go over my monthly events?',
    a: "Tracking stops for the rest of the cycle: events past your limit aren't captured and can't be recovered, though your dashboards stay fully available and tracking resumes next month. You're never charged a surprise overage. To keep capturing everything, turn on overage billing and pay per event.",
  },
  {
    q: 'Already have a free account?',
    a: 'Buy with the same email and apply the deal to your existing workspace, keeping all of your data.',
  },
];

function TierCard({
  tier,
  featured,
  onBuy,
  buying,
}: {
  tier: LtdPublicTier;
  featured: boolean;
  onBuy: () => void;
  buying: boolean;
}) {
  const perks = [
    `${compact(tier.eventBucket)} events / month`,
    `${tier.maxDomains} domains`,
    `${tier.maxUsers} team members`,
    `${tier.retentionMonths} months data retention`,
    `${tier.plan === 'PRO' ? 'Pro' : 'Essentials'} features, forever`,
  ];
  return (
    // Fixed responsive widths (not grid tracks) so a wrapping row with justify-center
    // centers a trailing partial row (e.g. the last 2 of 5 tiers) instead of left-aligning it.
    <Card
      className={cn(
        'h-full w-full sm:w-[calc(50%-0.625rem)] lg:w-[calc(33.333%-0.834rem)]',
        featured && 'border-primary',
      )}
    >
      <CardContent className="flex h-full flex-col gap-5 p-6">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-lg font-bold tracking-tight">{tier.label}</h3>
          {featured && (
            <Badge className="bg-primary text-primary-foreground ring-primary/40">
              Most Popular
            </Badge>
          )}
        </div>
        <div className="flex items-baseline gap-1.5">
          <span className="text-3xl font-bold tracking-tight">{money(tier.priceCents)}</span>
          <span className="text-muted-foreground text-sm">one-time</span>
        </div>
        <Separator />
        <ul className="flex-1 space-y-2.5 text-sm">
          {perks.map((perk) => (
            <li key={perk} className="flex items-start gap-2">
              <CheckIcon weight="bold" className="text-primary mt-0.5 size-4 shrink-0" />
              <span>{perk}</span>
            </li>
          ))}
        </ul>
        <Button
          onClick={onBuy}
          loading={buying}
          variant={featured ? 'primary' : 'outline'}
          className="w-full"
        >
          Get {tier.label}
        </Button>
      </CardContent>
    </Card>
  );
}

function DealPage() {
  const { data: offer, isLoading } = useQuery({
    queryKey: ['ltd-offer'],
    queryFn: LtdService.getOffer,
  });
  const [codeInput, setCodeInput] = useState('');
  const [unlockedCode, setUnlockedCode] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);

  const validate = useMutation({
    mutationFn: (code: string) => PromoService.validateCode(code),
    onSuccess: ({ valid }, code) =>
      valid ? setUnlockedCode(code) : setCodeError('That access code is not valid.'),
    onError: () => setCodeError('Could not check that code. Please try again.'),
  });

  const checkout = useMutation({
    mutationFn: ({ tierId, code }: { tierId: string; code: string }) =>
      LtdService.createCheckout(tierId, code),
    onSuccess: ({ url }) => {
      if (url) window.location.href = url; // hand off to Stripe Checkout
    },
  });

  // Keep this gated, short-lived launch page out of search indexes (DOM head sync).
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  const unlock = () => {
    const code = codeInput.trim();
    if (!code) return;
    setCodeError(null);
    validate.mutate(code);
  };

  return (
    <div className="bg-background text-foreground min-h-dvh">
      {/* Hero: the one expressive moment is the soft primary glow behind the title. */}
      <div className="relative overflow-hidden">
        <div
          aria-hidden
          className="bg-primary/20 pointer-events-none absolute inset-x-0 -top-32 mx-auto h-64 max-w-2xl rounded-full blur-3xl"
        />
        <div className="relative mx-auto max-w-2xl px-6 pt-20 pb-4 text-center">
          <div className="mb-10 flex justify-center">
            <LinkLogo />
          </div>
          <Badge className="mb-5">{unlockedCode ? 'Limited-Time Launch Offer' : 'Invite Only'}</Badge>
          <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
            {unlockedCode ? 'Logspot Lifetime Deal' : "You're Invited"}
          </h1>
          <p className="text-muted-foreground mx-auto mt-4 max-w-xl text-base sm:text-lg">
            {unlockedCode
              ? 'AI-native go-to-market analytics. Pay once, keep it for life. Choose the monthly event volume that fits, and never think about a subscription again.'
              : 'Enter your access code to unlock a special Logspot offer.'}
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-6 pb-20">
        {isLoading && (
          <div className="mt-16 flex justify-center">
            <Loader />
          </div>
        )}

        {!isLoading && !offer?.enabled && (
          <Card className="mx-auto mt-10 max-w-md">
            <CardContent className="p-8 text-center">
              <h2 className="text-xl font-bold tracking-tight">This Offer Has Ended</h2>
              <p className="text-muted-foreground mt-2 text-sm">
                The lifetime deal is not currently available. Thanks for your interest.
              </p>
            </CardContent>
          </Card>
        )}

        {!isLoading && offer?.enabled && !unlockedCode && (
          <Card className="mx-auto mt-10 max-w-md">
            <CardContent className="flex flex-col gap-3 p-8">
              <h2 className="text-center text-xl font-bold tracking-tight">
                Enter Your Access Code
              </h2>
              <p className="text-muted-foreground text-center text-sm">
                This deal is invite-only. Enter the code you were given to unlock pricing.
              </p>
              <Input
                value={codeInput}
                onChange={(e) => setCodeInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && unlock()}
                placeholder="Access code"
                autoFocus
              />
              {codeError && <p className="text-destructive text-sm">{codeError}</p>}
              <Button onClick={unlock} loading={validate.isPending} className="w-full">
                Unlock the Deal
              </Button>
            </CardContent>
          </Card>
        )}

        {!isLoading && offer?.enabled && unlockedCode && (
          <>
            <div className="mt-10 flex flex-wrap justify-center gap-5">
              {offer.tiers.map((tier, i) => (
                <TierCard
                  key={tier.id}
                  tier={tier}
                  featured={i === 2}
                  onBuy={() => checkout.mutate({ tierId: tier.id, code: unlockedCode })}
                  buying={checkout.isPending && checkout.variables?.tierId === tier.id}
                />
              ))}
            </div>
            {checkout.isError && (
              <p className="text-destructive mt-4 text-center text-sm">
                Could not start checkout. Please try again.
              </p>
            )}
            <p className="text-muted-foreground mt-6 text-center text-sm">
              One payment. No subscription. Every tier includes automatic tracking,
              funnels, journeys, and revenue analytics.
            </p>

            <div className="mx-auto mt-20 max-w-2xl">
              <h2 className="text-center text-xl font-bold tracking-tight">
                Frequently Asked Questions
              </h2>
              <div className="mt-8 space-y-6">
                {FAQ.map((item, i) => (
                  <div key={item.q}>
                    {i > 0 && <Separator className="mb-6" />}
                    <h3 className="font-medium">{item.q}</h3>
                    <p className="text-muted-foreground mt-1.5 text-sm">{item.a}</p>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
